import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSubmit: (data: LoginFormData) => void;
  isLoading?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  return (
    <Card className="bg-white shadow-2xl border-0 rounded-3xl overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white p-8">
        <div className="text-center">
          <CardTitle className="text-3xl font-bold mb-2">Admin Login</CardTitle>
          <CardDescription className="text-blue-100 text-lg">
            Enter your credentials to access the dashboard
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="p-8 bg-gradient-to-b from-white to-gray-50">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="email" className="text-sm font-semibold text-gray-800">
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="admin@example.com"
              {...register('email')}
              className="h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl shadow-sm"
            />
            {errors.email && (
              <p className="text-sm text-red-600 flex items-center mt-1">
                <span className="mr-1">⚠</span>
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <Label htmlFor="password" className="text-sm font-semibold text-gray-800">
              Password *
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              {...register('password')}
              className="h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl shadow-sm"
            />
            {errors.password && (
              <p className="text-sm text-red-600 flex items-center mt-1">
                <span className="mr-1">⚠</span>
                {errors.password.message}
              </p>
            )}
          </div>

          <div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-14 text-lg font-semibold bg-white text-blue-600 hover:bg-gray-50 transition-all duration-200 rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign In to Dashboard'
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};